import { Card, CardContent, Box, Typography } from "@mui/material";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/authContext";

// ─── Color por estado ─────────────────────────────────────────────────────────
const getStatusStyle = (text = "") => {
  if (text.includes("Oferta"))     return { color: "#059669", bg: "#ecfdf5", border: "#6ee7b7" };
  if (text.includes("Lista"))      return { color: "#9333ea", bg: "#faf5ff", border: "#d8b4fe" };
  if (text.includes("Disponible")) return { color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" };
  if (text.includes("Ultimas"))    return { color: "#ea580c", bg: "#fff7ed", border: "#fdba74" };
  if (text.includes("Sin Stock"))  return { color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0" };
  return { color: "#059669", bg: "#ecfdf5", border: "#6ee7b7" };
};

const ProductCard = ({ data, sty, children }) => {
  const navigate    = useNavigate();
  const handleClick = () => navigate(`/product/${data.id}`, { state: { productData: data } });

  if (!data) return null;

  const hayOferta = Number(data?.percentage) > 0;
  const { color, bg, border } = getStatusStyle(data.installment || "");

  return (
    <Card
      sx={{
        boxShadow: "none",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1.5px solid var(--border, #f1f5f9)",
        borderRadius: "var(--radius-lg, 14px)",
        overflow: "hidden",
        bgcolor: "var(--bg-card, #fff)",
        cursor: "pointer",
        position: "relative",
        transition: "all 0.22s cubic-bezier(0.25,0.46,0.45,0.94)",
        "&:hover": {
          borderColor: color,
          boxShadow: `0 8px 28px ${color}22`,
          transform: "translateY(-3px)",
          "& .card-img": { transform: "scale(1.07)" },
        },
        ...sty,
      }}
      onClick={handleClick}
    >
      {/* ── Badge descuento ── */}
      {hayOferta && (
        <Box sx={{
          position: "absolute", top: 8, left: 8, zIndex: 3,
          bgcolor: color, color: "#fff",
          fontWeight: 800, fontSize: "0.62rem",
          fontFamily: "var(--font-heading, inherit)",
          px: "7px", py: "2px",
          borderRadius: "20px",
          boxShadow: `0 2px 8px ${color}55`,
          lineHeight: 1.5, letterSpacing: "0.02em",
        }}>
          -{data.percentage}%
        </Box>
      )}

      {/* ── Imagen ── */}
      <Box sx={{
        position: "relative",
        bgcolor: "var(--bg-subtle, #f8fafc)",
        height: { xs: 80, sm: 95, md: 110 },
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        p: { xs: "8px", md: "10px" },
      }}>
        <Box
          component="img"
          className="card-img"
          src={data.imgSrc}
          alt={data.title}
          sx={{
            width: "100%", height: "100%",
            objectFit: "contain",
            transition: "transform 0.35s ease",
            display: "block",
          }}
        />
        {/* Gradiente inferior */}
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "28px",
          background: "linear-gradient(transparent, var(--bg-card, #fff))",
          pointerEvents: "none",
        }} />
      </Box>

      {/* ── Contenido ── */}
      <CardContent sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        p: { xs: "8px 8px 10px !important", md: "10px 12px 12px !important" },
      }}>

        {/* Categoría */}
        {data.category && (
          <Typography sx={{
            fontSize: "0.6rem", fontWeight: 700,
            color: "var(--text-muted, #94a3b8)",
            textTransform: "uppercase", letterSpacing: "0.08em",
            lineHeight: 1, mb: "3px",
            fontFamily: "var(--font-body, inherit)",
          }}>
            {data.category}
          </Typography>
        )}

        {/* Título */}
        <Typography sx={{
          fontWeight: 600,
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          color: "var(--text-primary, #1e293b)",
          lineHeight: 1.35,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          fontFamily: "var(--font-body, inherit)",
          mb: "3px",
          transition: "color 0.15s",
          ".MuiCard-root:hover &": { color },
        }}>
          {data.title}
        </Typography>

        {/* Código */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: "6px" }}>
          <Typography sx={{
            fontSize: "0.62rem",
            color: "var(--text-muted, #cbd5e1)",
            fontWeight: 600,
            fontFamily: "var(--font-mono, monospace)",
            lineHeight: 1,
          }}>
            COD {data.code}
          </Typography>
          {children}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Precio viejo tachado */}
        {hayOferta && data.priceOld && (
          <Typography sx={{
            fontSize: "0.68rem",
            color: "var(--price-old, #94a3b8)",
            textDecoration: "line-through",
            lineHeight: 1.2,
            fontFamily: "var(--font-mono, monospace)",
            mb: "2px",
          }}>
            {data.priceOld}
          </Typography>
        )}

        {/* Precio principal */}
        <Typography sx={{
          fontSize: { xs: "0.88rem", md: "0.95rem" },
          fontWeight: 800,
          color,
          fontFamily: "var(--font-heading, inherit)",
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mb: "8px",
        }}>
          {data.precioMostrar}
        </Typography>

        {/* Badge estado */}
        <Box sx={{
          display: "inline-flex",
          alignSelf: "flex-start",
          px: "6px", py: "2px",
          bgcolor: bg,
          color,
          border: `1px solid ${border}`,
          borderRadius: "20px",
          fontWeight: 700,
          fontSize: "0.58rem",
          fontFamily: "var(--font-body, inherit)",
          letterSpacing: "0.02em",
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        }}>
          {data.installment}
        </Box>

      </CardContent>
    </Card>
  );
};

export default ProductCard;